import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router';
import linkTo from 'linkTo';
import getCsrfToken from '@99designs/common/utils/app/getCsrfToken';
import Box from 'libs/core-components/Box/Box';
import { SolidAnchorButton } from 'libs/core-components/SolidButton/SolidAnchorButton/SolidAnchorButton';
import { SolidButton } from 'libs/core-components/SolidButton/SolidButton/SolidButton';
import Stack from 'libs/core-components/Stack/Stack';
import is99StaffEmail from './common/is99StaffEmail';
import { Layout } from './common/Layout';

import Bugsnag from '@bugsnag/js';

import { useAppConfig } from 'common/AppConfigContext';
import Loading from 'common/Loading';
import Link from 'common/Link/BaseLink';
import NoWrap from 'common/NoWrap';
import redirectTo from 'common/Ssr/redirectTo';
import Masquerading from './Masquerading';
import TermsUpdate from './TermsUpdate';
import { useForm } from 'react-hook-form';
import BaseRouterLink from 'common/Link/BaseRouterLink';
import TextField from './TextField';
import { getErrorForField, setBackendErrors } from './common/utils';
import fetchPreAuthorisation from './fetchPreAuthorisation';
import Captcha from './Captcha';
import SuccessAlert from 'libs/core-components/Alert/SuccessAlert/SuccessAlert';

interface LoginProps {
    showRecaptcha?: boolean;
}

interface FormState {
    email: string;
    password: string;
    rememberMe: string;
}

const CheckboxInput = styled.input`
    /* Increase specificity */
    && {
        width: 16px;
        height: 16px;
        margin-right: 1em;
    }
`;

function LoginContainer() {
    const { search } = useLocation();
    const [loading, setLoading] = useState<boolean>(true);
    const [showRecaptcha, setShowRecaptcha] = useState<boolean>(true);
    const [showMasquerading, setShowMasquerading] = useState<boolean>(false);

    useEffect(() => {
        fetchPreAuthorisation(search).then((data) => {
            if (data.target) {
                redirectTo(data.target);
                return;
            } else if (data.masquerading) {
                setShowMasquerading(true);
            } else if (data.skip_recaptcha) {
                setShowRecaptcha(false);
            }

            setLoading(false);
        });
    }, []);

    if (loading) {
        return <Loading />;
    }

    if (showMasquerading) {
        return <Masquerading />;
    }

    return <Login showRecaptcha={showRecaptcha} />;
}

export function Login({ showRecaptcha = true }: LoginProps) {
    const { recaptchaSiteKey } = useAppConfig();

    const { search } = useLocation();
    const recaptchaRef = useRef<any>(null);
    const [params] = useState(() => {
        return new URLSearchParams(search);
    });
    const [postLoginUrl, setPostLoginUrl] = useState('');
    const [recaptchaError, setRecaptchaError] = useState('');
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const [showUpdatedTerms, setShowUpdatedTerms] = useState(false);
    const [force2FA, setForce2FA] = useState<boolean>(false);
    const {
        register,
        handleSubmit,
        watch,
        errors,
        formState: { isSubmitting },
        setError,
    } = useForm<FormState>({
        defaultValues: {
            email: '',
        },
    });

    const showOktaButton = is99StaffEmail(watch('email'));
    const resetSuccessReason = params.get('reason');
    const resetSuccessMessageMap: Record<string, string> = {
        'password-reset': __('Password updated. Please log in to continue.'),
        'email-updated': __('Email updated. Please log in to continue.'),
    };

    function handleRecaptchaChange(token: string) {
        setRecaptchaError('');
        setRecaptchaToken(token);
    }

    function handleRecaptchaError() {
        setForce2FA(true);
    }

    function onSubmit({ email, password, rememberMe }: FormState) {
        const needsRecaptcha = !force2FA && showRecaptcha && !recaptchaToken;
        if (recaptchaSiteKey && needsRecaptcha) {
            setRecaptchaError(__("Confirm that you're not a robot"));
            return;
        }

        const paramString = params.toString();
        const url = new URL(
            `/accounts/xhr/login${paramString ? `?${paramString}` : ''}`,
            window.location.origin
        );

        return fetch(url.toString(), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
                email,
                password,
                rememberMe,
                recaptchaToken,
                force_2fa: force2FA,
                csrf_token: getCsrfToken(),
            }),
        })
            .then((response) => response.json().then((d) => ({ status: response.status, body: d })))
            .then((d) => {
                if (d.status === 200) {
                    if (d.body.must_verify_2fa) {
                        //Intentionally page load rather than push to help with resolving cookie
                        //TODO this doesn't need to be a route change inside SPA, what's the deal with the cookie?
                        window.location.assign(d.body.target);
                        return;
                    }

                    if (d.body.must_view_terms) {
                        setPostLoginUrl(d.body.target);
                        setShowUpdatedTerms(true);
                        return;
                    }
                    window.location.assign(d.body.target);
                    return;
                }

                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }

                if (d.body.errors) {
                    setBackendErrors(d.body.errors, setError);
                    return;
                }
            })
            .catch((e) => {
                Bugsnag.notify(e, (event) => {
                    event.context = 'Logging in';
                });
            });
    }

    if (showUpdatedTerms) {
        return <TermsUpdate redirectUrl={postLoginUrl} />;
    }

    return (
        <Layout
            title={__('Log in')}
            description={__('Log in to your 99designs account')}
            heading={__('Log in')}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack space="space8">
                    {resetSuccessReason && resetSuccessMessageMap[resetSuccessReason] && (
                        <SuccessAlert data-testid="reset-success-message">
                            {resetSuccessMessageMap[resetSuccessReason]}
                        </SuccessAlert>
                    )}
                    <TextField
                        error={getErrorForField('email', errors)}
                        name="email"
                        type="text"
                        label={__('Email')}
                        ref={register({ required: __('Enter your email address') })}
                        autoFocus
                        autoComplete="username"
                    />

                    <TextField
                        error={getErrorForField('password', errors)}
                        name="password"
                        type="password"
                        label={__('Password')}
                        ref={register({ required: __('Enter your password') })}
                        autoComplete="current-password"
                    />

                    <Box display="flex" justifyContent="space-between">
                        <div className="field field--checkable field--no-margin-bottom">
                            <CheckboxInput
                                ref={register}
                                className="input-checkbox"
                                id="remember_me"
                                name="rememberMe"
                                type="checkbox"
                            />
                            <label htmlFor="remember_me">{__('Remember me')}</label>
                        </div>
                        <NoWrap>
                            <Link href={linkTo.forgotPassword()}>{__('Forgot password?')}</Link>
                        </NoWrap>
                    </Box>
                    {showRecaptcha && !!recaptchaSiteKey && (
                        <Captcha
                            ref={recaptchaRef}
                            siteKey={recaptchaSiteKey}
                            onChange={handleRecaptchaChange}
                            onErrored={handleRecaptchaError}
                            error={recaptchaError || getErrorForField('captcha', errors)}
                        />
                    )}

                    <SolidButton
                        data-testid="login-button"
                        name="login-button"
                        processing={isSubmitting}
                        size="large"
                        variant="primary"
                        width="fill"
                    >
                        {__('Log in')}
                    </SolidButton>

                    {showOktaButton && (
                        <SolidAnchorButton
                            href={linkTo.authSAMLRedirect(
                                params.get('target') || params.get('from')
                            )}
                            data-testid="okta-button"
                            size="large"
                            variant="secondary"
                            width="fill"
                        >
                            Log in via Okta
                        </SolidAnchorButton>
                    )}
                </Stack>
            </form>
            <p>
                {__("Don't have an account?")}{' '}
                <BaseRouterLink to={linkTo.signup()}>{__('Sign up')}</BaseRouterLink>
            </p>
        </Layout>
    );
}
export default LoginContainer;
